import React, { DOMElement, useEffect, useState } from "react";
import "./sidebar.scss";
import Sider from "antd/es/layout/Sider";
import { ConfigProvider, Menu, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { routesConfig } from "../../config/routes";
import { isEmpty } from "lodash";
import { globalStore } from "../../stores/stores";
import { getDetailAdminInterface } from "../../service/admin-interface";
import { getContrastingColor } from "../../utils/functionUltils";
import { MenuProps } from "antd/lib";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import enLocale from "../../i18n/locales/en.json";

// Reverse dictionary mapping English strings back to Vietnamese base keys
const enToViMap: Record<string, string> = {};
Object.entries(enLocale).forEach(([viKey, enVal]) => {
  if (typeof enVal === "string" && enVal.trim() && !enToViMap[enVal.trim()]) {
    enToViMap[enVal.trim()] = viKey.trim();
  }
});

interface SidebarProps {
  selectedMenu: any[];
  collapsedContent: boolean;
  isHideSider: boolean;
  siderContent?: React.ReactNode;
}

const SiderLayout: React.FC<SidebarProps> = ({
  selectedMenu,
  collapsedContent,
  isHideSider,
  siderContent
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("en") ? "en" : "vi";
  const navigate = useNavigate();
  const { reloadAdminInterface, personalInfo } = globalStore();

  const getTranslatedLabel = (raw: any): any => {
    if (typeof raw !== "string") return raw;
    const trimmed = raw.trim();
    if (!trimmed) return raw;

    if (currentLang === "vi") {
      // If label is an English string, translate back to Vietnamese via reverse map
      if (enToViMap[trimmed]) {
        return enToViMap[trimmed];
      }
      return t(trimmed, trimmed);
    } else {
      // In English mode: translate from Vietnamese key to English if available
      const translated = t(trimmed, trimmed);
      if (translated !== trimmed) return translated;
      return trimmed;
    }
  };

  const location = useLocation();
  const pathName = location.pathname;
  const defaultOpenKeys = location.pathname.split("/").filter((x) => x !== "");
  const defaultSelectedKeys = [
    `/` +
    `${defaultOpenKeys[0]}/` +
    `${!isEmpty(defaultOpenKeys[1]) ? defaultOpenKeys[1] : ""}`,
  ];
  const listOpenKeys = [`${defaultOpenKeys[0]}`, `${defaultOpenKeys[1]}`];

  const [loading, setLoading] = useState<boolean>(false);
  const [color, setColor] = useState<any>({});
  const [backgroundColor, setBackgroundColor] = useState<string>("");

  const getColor = async () => {
    if (personalInfo?.color) {
      setColor(personalInfo?.color);
    }
  };

  useEffect(() => {
    getColor();
  }, [reloadAdminInterface, personalInfo]);

  const getColorDetail = async () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const id: any = urlParams.get("interfaceId");
    if (id) {
      await getDetailAdminInterface(id).then((res) => {
        if (res.status === 200) {
          setBackgroundColor(res.data?.data?.color?.menuColor);
        }
      });
    }
  };

  useEffect(() => {
    getColorDetail();
  }, [navigate, reloadAdminInterface]);
  const contrastingTextColor = getContrastingColor(color?.menuColor);

  const renderMenuItems = (menuItems: any[]): MenuProps["items"] => {
    if (!menuItems || !Array.isArray(menuItems)) return [];
    return menuItems.map((item: any) => {
      const rawLabel =
        typeof item.label === "string"
          ? item.label
          : typeof item.label?.props?.children === "string"
          ? item.label.props.children
          : "Menu Item";

      const tooltipLabel = getTranslatedLabel(rawLabel);

      return {
        ...item,
        label: (
          <Tooltip title={tooltipLabel} placement="right">
            <span>{tooltipLabel}</span>
          </Tooltip>
        ),
        key: item.key,
        children: item.children ? renderMenuItems(item.children) : undefined,
      };
    });
  };

  const goBack = () => {
    navigate(`${routesConfig.courseManagement}/${routesConfig.courseList}`);
  };
  return (
    <>
      {!isHideSider ? (
        <ConfigProvider
          theme={{
            components: {
              Menu: { colorText: contrastingTextColor },
            },
          }}
        >
          <Sider
            breakpoint="lg"
            width="100%"
            style={{
              height: "100%",
              boxShadow: "unset",
              background: `${pathName.includes(routesConfig.detailAdminInterface)
                ? backgroundColor
                : !isEmpty(color) && !loading
                  ? color?.menuColor
                  : "#9F224E"
                }`,
            }}
            className="slider-bar-theme"
            collapsible={!isHideSider}
            collapsed={collapsedContent}
            trigger={null}
          >
            {siderContent && (
              <div
                className="menu-description"
                style={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "12px",
                  background: backgroundColor,
                  color: contrastingTextColor,
                }}
              >

                <div className="flex gap-16">
                  <ArrowLeftOutlined
                    style={{ fontSize: "16px", cursor: "pointer" }}
                    onClick={goBack}
                  />
                  {!collapsedContent ? (
                    <div className="text-lg">{typeof siderContent === "string" ? getTranslatedLabel(siderContent) : siderContent}</div>
                  ) : null}
                </div>

              </div>
            )}
            <Menu
              mode="inline"
              selectedKeys={[pathName]}
              defaultSelectedKeys={defaultSelectedKeys}
              defaultOpenKeys={listOpenKeys}
              items={renderMenuItems(selectedMenu)}
              className="menu-wrapper"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                paddingBottom: "48px",
                height: "100%",
                background: `${pathName.includes(routesConfig.detailAdminInterface)
                  ? backgroundColor
                  : !isEmpty(color) && !loading
                    ? color?.menuColor
                    : "#9F224E"
                  }`,
              }}
              onClick={(e) => {
                const baseUrl = "https://";
                if (e.key.startsWith(baseUrl)) {
                  window.location.href = e.key;
                } else {
                  navigate(e.key);

                }
              }}
            />

          </Sider>
        </ConfigProvider>
      ) : null}
    </>
  );
};

export default SiderLayout;
